import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Activity, TrendingUp, TrendingDown, Send, ShieldCheck, Wallet, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SignalTerminal from '../components/SignalTerminal';
import { CONFIG, TRADING_PAIRS } from '../mock/mock';
import { api } from '../lib/api';

export default function Dashboard() {
  const current = api.getCurrent();
  const [history, setHistory] = useState([]);
  const [utc, setUtc] = useState('');

  useEffect(() => {
    const t = setInterval(() => setUtc(new Date().toISOString().substring(11, 19)), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Seed mock history
    const seed = Array.from({ length: 6 }).map((_, i) => {
      const dir = Math.random() > 0.5 ? 'CALL' : 'PUT';
      const pair = TRADING_PAIRS[Math.floor(Math.random() * TRADING_PAIRS.length)];
      return {
        pair,
        direction: dir,
        confidence: 88 + Math.floor(Math.random() * 11),
        result: Math.random() > 0.25 ? 'WIN' : 'LOSS',
        time: `${String(14 - i).padStart(2, '0')}:${String(30 - i * 3).padStart(2, '0')}`,
      };
    });
    setHistory(seed);
  }, []);

  if (!current) return <Navigate to="/login" replace />;

  return (
    <div className="alpha-bg min-h-screen">
      <Header />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <div className="text-[11px] tracking-[0.35em] text-cyan-300/80">AI SIGNAL DASHBOARD</div>
            <h1 className="font-display mt-1 text-3xl sm:text-4xl font-bold text-white">Welcome, Trader <span className="text-cyan-gradient">#{current}</span></h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge icon={ShieldCheck} text="Verified" tone="emerald" />
            <Badge icon={Activity} text="AI Engine · Online" tone="cyan" />
            <Badge icon={Clock} text={`${utc} UTC`} tone="slate" />
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SignalTerminal interactive={true} autoRotate={false} />
          </div>

          <div className="space-y-6">
            <div className="glass rounded-2xl p-5">
              <div className="text-xs tracking-[0.3em] text-cyan-300/80">ACCOUNT</div>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-11 w-11 rounded-lg glass flex items-center justify-center glow-cyan">
                  <Wallet className="h-5 w-5 text-cyan-300" />
                </div>
                <div>
                  <div className="text-white font-mono">ID {current}</div>
                  <div className="text-xs text-slate-400">{CONFIG.brokerName} · Verified</div>
                </div>
              </div>
              <a href={CONFIG.referralLink} target="_blank" rel="noreferrer" className="mt-4 btn-ghost rounded-lg px-4 py-2 text-xs tracking-widest uppercase inline-flex items-center gap-2 w-full justify-center">
                Open {CONFIG.brokerName}
              </a>
            </div>

            <div className="glass rounded-2xl p-5">
              <div className="text-xs tracking-[0.3em] text-cyan-300/80 mb-3">QUICK ACTIONS</div>
              <div className="grid grid-cols-2 gap-2">
                <a href={CONFIG.telegramLink} target="_blank" rel="noreferrer" className="btn-cyan rounded-lg px-3 py-2 text-xs tracking-widest uppercase inline-flex items-center justify-center gap-2">
                  <Send className="h-3.5 w-3.5" /> Telegram
                </a>
                <Link to="/" className="btn-ghost rounded-lg px-3 py-2 text-xs tracking-widest uppercase text-center">Home</Link>
              </div>
            </div>

            <div className="glass rounded-2xl p-5">
              <div className="text-xs tracking-[0.3em] text-cyan-300/80">RECENT SIGNALS</div>
              <ul className="mt-3 divide-y divide-cyan-400/10">
                {history.map((h, i) => (
                  <li key={i} className="py-3 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {h.direction === 'CALL' ? <TrendingUp className="h-4 w-4 text-emerald-300" /> : <TrendingDown className="h-4 w-4 text-rose-300" />}
                      <span className="font-mono text-slate-200">{h.pair}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 font-mono">{h.time}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${h.result === 'WIN' ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10' : 'text-rose-300 border-rose-400/40 bg-rose-500/10'}`}>{h.result}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          <MiniStat label="Today's Signals" value="12" />
          <MiniStat label="Win Rate" value="87%" accent />
          <MiniStat label="Avg Confidence" value="94%" />
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Badge({ icon: Icon, text, tone = 'cyan' }) {
  const tones = {
    emerald: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10',
    cyan: 'text-cyan-300 border-cyan-400/40 bg-cyan-500/10',
    slate: 'text-slate-300 border-slate-500/40 bg-slate-500/10',
  };
  return (
    <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${tones[tone]}`}>
      <Icon className="h-3.5 w-3.5" /> {text}
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="text-[11px] tracking-[0.3em] text-slate-400">{label}</div>
      <div className={`mt-2 font-display text-3xl font-bold ${accent ? 'text-cyan-gradient' : 'text-white'}`}>{value}</div>
    </div>
  );
}
